import { serverApi, handleApiResponse } from "@core/api/core"
import type { PatientClassification, PatientClassificationFields } from "@/types/patientClassification"
import type { PatientDetailSerializer } from "@/types/patient"

export type PatientClassificationDisplay = NonNullable<PatientDetailSerializer["patientClassification"]>
export type PatientClassificationOption = Pick<PatientClassificationDisplay, "code" | "name" | "emoji">

export const getPatientClassificationOptions = async (patientUuid: string): Promise<PatientClassificationOption[]> => {
  const response = await serverApi.get(`/patient/${patientUuid}/classification/`)
  return handleApiResponse<PatientClassificationOption[]>(response)
}

export const savePatientClassification = async (patientUuid: string, code: number): Promise<PatientClassificationDisplay> => {
  const response = await serverApi.patch(`/patient/${patientUuid}/classification/`, { code })
  return handleApiResponse<PatientClassificationDisplay>(response)
}

export const PATIENT_CLASSIFICATION_CHANGED_EVENT = "patient-classification-changed"

export const getPatientClassifications = async (): Promise<PatientClassification[]> => {
  const response = await serverApi.get("/patient/classifications/")
  return handleApiResponse<PatientClassification[]>(response)
}

export const createPatientClassification = async (fields: PatientClassificationFields): Promise<PatientClassification> => {
  const response = await serverApi.post("/patient/classifications/", fields)
  return handleApiResponse<PatientClassification>(response)
}

export const updatePatientClassification = async (code: number, fields: Partial<PatientClassificationFields>): Promise<PatientClassification> => {
  const response = await serverApi.patch(`/patient/classifications/${code}/`, fields)
  return handleApiResponse<PatientClassification>(response)
}

export const savePatientClassificationOrder = async (codes: number[]): Promise<void> => {
  const response = await serverApi.post("/patient/classifications/order/", { codes })
  return handleApiResponse<void>(response)
}

export const uploadPatientClassificationImage = async (code: number, file: File): Promise<PatientClassification> => {
  const payload = new FormData()
  payload.append("file", file)
  const response = await serverApi.post(`/patient/classifications/${code}/image/`, payload)
  return handleApiResponse<PatientClassification>(response)
}

export const clearPatientClassificationImage = async (code: number): Promise<PatientClassification> => {
  const response = await serverApi.post(`/patient/classifications/${code}/clear-image/`, {})
  return handleApiResponse<PatientClassification>(response)
}

export const getPatientClassificationImage = async (code: number): Promise<Blob> => {
  const response = await serverApi.get(`/patient/classifications/${code}/image/`, { responseType: "blob" })
  return response.data
}
